/**
 * The first-run setup steps as the web app shows them: label, hint, screen and permission for each
 * step the API reports, the six "core" steps the Getting started panel counts, and the areas an
 * owner can pick first on the welcome screen. Pure (no React), so Home, the Setup page, the sidebar
 * item and the welcome setup all agree.
 */

import {
  SETUP_STEP_KEYS,
  type ModuleKey,
  type Permission,
  type SetupFocusArea,
  type SetupStepKey,
} from '@hrms/shared';

/**
 * What each step asks for, where it's done, and the permission that screen needs. The API only
 * says which are done and which apply to this organisation's plan.
 */
export const SETUP_STEPS: Record<
  SetupStepKey,
  { label: string; hint: string; to: string; needs: Permission; blockedHint?: string }
> = {
  legal_entity: {
    label: 'Add your company’s legal entity',
    hint: 'Its country, currency and financial year. Employees and payroll belong to one.',
    to: '/admin/organisation/company-profile',
    needs: 'legal_entity:create',
    blockedHint: 'Only a Global Admin can register the company. Ask them to add it first.',
  },
  company_details: {
    label: 'Confirm your company details',
    hint: 'We filled in the country, currency and financial year for you. Check them and add the registered address.',
    to: '/admin/organisation/company-profile',
    needs: 'legal_entity:update',
    blockedHint: 'Only a Global Admin can change the company details.',
  },
  locations: {
    label: 'Add your office locations',
    hint: 'Each location’s time zone decides “today” for attendance and holidays.',
    to: '/admin/organisation/branch',
    needs: 'location:manage',
  },
  departments: {
    label: 'Create your departments',
    hint: 'Used for reporting, approvals and access.',
    to: '/admin/organisation/department',
    needs: 'department:manage',
  },
  grades: {
    label: 'Define your grades',
    hint: 'Levels such as L1 to L5. Salary structures and policies can follow them.',
    to: '/admin/organisation/grades',
    needs: 'org.settings:update',
  },
  designations: {
    label: 'Add designations',
    hint: 'Job titles people are hired into.',
    to: '/admin/organisation/post',
    needs: 'post:manage',
  },
  employee_id_series: {
    label: 'Set your employee ID format',
    hint: 'For example EMP0001. Without one, new hires get random IDs.',
    to: '/admin/employee/id-series',
    needs: 'org.settings:update',
  },
  probation_policy: {
    label: 'Set a probation policy',
    hint: 'How long probation lasts and whether it ends on its own.',
    to: '/admin/organisation/probation-policies',
    needs: 'org.settings:update',
  },
  holidays: {
    label: 'Set up this year’s holidays',
    hint: 'So leave and attendance skip them.',
    to: '/leave/holidays',
    needs: 'holiday_calendar:manage',
  },
  shifts: {
    label: 'Set your working hours',
    hint: 'Shifts decide when people are late, present or absent.',
    to: '/attendance/shifts',
    needs: 'shift:manage',
  },
  owner_employment: {
    label: 'Add yourself as an employee',
    hint: 'So you can use leave, attendance and payslips too.',
    to: '/admin/employee/list',
    needs: 'employee:create',
  },
  employees: {
    label: 'Add or import your employees',
    hint: 'One at a time, or many from a spreadsheet.',
    to: '/admin/employee/list',
    needs: 'employee:create',
  },
  team_signed_in: {
    label: 'Get your team signed in',
    hint: 'Everyone you add gets an email invite; resend one from the employee’s menu.',
    to: '/admin/employee/list',
    needs: 'employee:update',
  },
  first_punch: {
    label: 'Test your first mobile punch-in',
    hint: 'Clock in from the phone app once, so you know your team can mark attendance from theirs.',
    to: '/attendance/my',
    needs: 'attendance:punch',
  },
};

/**
 * The four steps every company does first, in order. A fifth comes from what the owner picked on
 * the welcome screen (see `focusStep`), and the sixth is always adding the team.
 */
const CORE_BASE: readonly SetupStepKey[] = [
  'company_details',
  'locations',
  'departments',
  'employee_id_series',
];

/** The setup step each focus area starts with, when it has one in the checklist. */
const FOCUS_STEP: Partial<Record<SetupFocusArea, SetupStepKey>> = {
  attendance: 'shifts',
  leave: 'holidays',
};

/**
 * The fifth core step: the first picked area that has a checklist step which applies to this plan;
 * otherwise holidays, then shifts, then designations - whichever applies first.
 */
export function focusStep(
  focusAreas: readonly SetupFocusArea[],
  applicable: ReadonlySet<SetupStepKey>,
): SetupStepKey {
  for (const area of focusAreas) {
    const step = FOCUS_STEP[area];
    if (step && applicable.has(step)) return step;
  }
  for (const fallback of ['holidays', 'shifts', 'designations'] as const) {
    if (applicable.has(fallback)) return fallback;
  }
  return 'designations';
}

/**
 * The six steps the Getting started panel counts, in order, keeping only steps that apply to this
 * plan. "Add your team" is last because invites go out the moment people are added, and the earlier
 * steps (locations, departments, ID format) are what a new employee record needs.
 */
export function coreSteps(
  focusAreas: readonly SetupFocusArea[],
  applicable: ReadonlySet<SetupStepKey>,
): SetupStepKey[] {
  const steps = [...CORE_BASE, focusStep(focusAreas, applicable), 'employees' as const];
  return [...new Set(steps)].filter((key) => applicable.has(key));
}

/** Every other applicable step, in the checklist's own order: shown under "More setup". */
export function moreSteps(
  core: readonly SetupStepKey[],
  applicable: ReadonlySet<SetupStepKey>,
): SetupStepKey[] {
  const inCore = new Set(core);
  return SETUP_STEP_KEYS.filter((key) => applicable.has(key) && !inCore.has(key));
}

/** One picked area: what the welcome screen says about it and where its own setup starts. */
export interface FocusAreaInfo {
  label: string;
  description: string;
  /** The module that has to be on for the link to work. */
  module: ModuleKey;
  /** Where that area is set up. */
  to: string;
  needs: Permission | readonly Permission[];
}

export const FOCUS_AREAS: Record<SetupFocusArea, FocusAreaInfo> = {
  attendance: {
    label: 'Attendance',
    description: 'Shifts, clock-in, late marks and monthly muster.',
    module: 'attendance',
    to: '/attendance/shifts',
    needs: ['shift:manage', 'attendance:update', 'org.settings:read'],
  },
  leave: {
    label: 'Leave',
    description: 'Leave types, balances, holidays and approvals.',
    module: 'leave',
    to: '/leave/policies',
    needs: 'leave.policy:manage',
  },
  payroll: {
    label: 'Payroll',
    description: 'Salary structures, statutory deductions and payslips.',
    module: 'statutory_payroll',
    to: '/admin/payroll/settings/salary-configuration',
    needs: 'payroll:read',
  },
  hiring: {
    label: 'Hiring',
    description: 'Job openings, candidates, interviews and offers.',
    module: 'ats_recruitment',
    to: '/admin/recruitment/requisitions',
    needs: 'requisition:read',
  },
  expenses: {
    label: 'Expenses',
    description: 'Claim categories, limits and reimbursements.',
    module: 'expense_reimbursement',
    to: '/admin/reimbursement',
    needs: 'expense.policy:manage',
  },
  performance: {
    label: 'Performance',
    description: 'Goals, review cycles and feedback.',
    module: 'performance_okr',
    to: '/admin/performance/review-cycles/setup',
    needs: 'review.cycle:manage',
  },
};

/** Query string a setup step's screen is opened with, so it can show "Back to setup". */
export const FROM_SETUP_PARAM = 'from=setup';

/** The link for a step, marked as coming from setup. */
export function stepHref(key: SetupStepKey): string {
  const to = SETUP_STEPS[key].to;
  return `${to}${to.includes('?') ? '&' : '?'}${FROM_SETUP_PARAM}`;
}
